import { ApiError } from "./types";

export interface MeetingResponse<T> {
  data: T;
  demoMode: boolean;
  correlationId: string | null;
}

export interface MeetingClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export type MeetingState = "DRAFT" | "IN_REVIEW" | "ADOPTED" | "SEALED" | "ABANDONED";
export type MeetingKind = "internal" | "customer" | "vendor" | "board" | "governance_review";
export type ConfidentialityClassification = "public" | "internal" | "confidential" | "restricted";
export type CandidateKind = "decision" | "commitment" | "owner" | "due_date";
export type CandidateDisposition = "PENDING" | "CONFIRMED" | "REJECTED";
export type CommitmentStatus = "OPEN" | "DUE_SOON" | "OVERDUE" | "COMPLETE" | "CANCELLED";
export type MeetingParticipantRole = "chair" | "minute_taker" | "participant" | "apology";

export interface FactAnchor {
  sourceId: string;
  documentVersion: number;
  contentHash: string;
  startOffset: number;
  endOffset: number;
  quote: string;
}

export interface MeetingParticipant {
  id: string;
  organizationId: string;
  meetingId: string;
  participantKind: "internal" | "external";
  userId: string | null;
  externalName: string | null;
  externalEmail: string | null;
  role: MeetingParticipantRole;
  addedBy: string;
  addedAt: string;
  removedBy: string | null;
  removedAt: string | null;
}

export type MeetingParticipantInput =
  | {
      participantKind: "internal";
      userId: string;
      role: MeetingParticipantRole;
    }
  | {
      participantKind: "external";
      externalName: string;
      externalEmail: string;
      role: MeetingParticipantRole;
    };

export interface MeetingTransition {
  id: string;
  organizationId: string;
  meetingId: string;
  fromState: MeetingState;
  toState: MeetingState;
  actorId: string;
  reason: string;
  revision: number;
  createdAt: string;
}

export interface MeetingSource {
  id: string;
  organizationId: string;
  meetingId: string;
  documentId: string;
  documentVersion: number;
  contentHash: string;
  mediaType: string;
  filename: string;
  byteSize: number;
  extractedCharacterCount: number;
  confidentialityClassification: ConfidentialityClassification;
  attachedBy: string;
  attachedAt: string;
  detachedBy: string | null;
  detachedAt: string | null;
  detachmentReason: string | null;
}

export interface Meeting {
  id: string;
  organizationId: string;
  title: string;
  purpose: string;
  createdBy: string;
  kind: MeetingKind;
  scheduledStart: string;
  actualStart: string | null;
  actualEnd: string | null;
  timezone: string;
  confidentialityClassification: ConfidentialityClassification;
  calendarEventRef: string | null;
  seriesRef: string | null;
  state: MeetingState;
  revision: number;
  createdAt: string;
  updatedAt: string;
  participants?: MeetingParticipant[];
  transitions?: MeetingTransition[];
}

export interface MeetingCandidate {
  id: string;
  organizationId: string;
  extractionId: string;
  meetingId: string;
  kind: CandidateKind;
  statement: string;
  proposedOwnerHint: string | null;
  proposedDueDate: string | null;
  basis: string;
  confidence: number;
  anchorSourceId: string;
  anchorDocumentVersion: number;
  anchorContentHash: string;
  anchorStartOffset: number;
  anchorEndOffset: number;
  anchorQuote: string;
  disposition: CandidateDisposition;
  dispositionActor: string | null;
  dispositionAt: string | null;
  dispositionReason: string | null;
}

export interface MeetingDecision {
  id: string;
  organizationId: string;
  meetingId: string;
  statement: string;
  correctedStatement: string | null;
  confirmingActor: string;
  confirmedAt: string;
  origin: "CONFIRMED_CANDIDATE" | "ASSERTED";
  candidateId: string | null;
  overrideReason: string | null;
  anchor: FactAnchor | null;
  supersedesDecisionId: string | null;
  supersededByDecisionId: string | null;
  supersededAt: string | null;
}

export interface MeetingCommitment {
  id: string;
  organizationId: string;
  meetingId: string;
  statement: string;
  correctedStatement: string | null;
  confirmingActor: string;
  confirmedAt: string;
  origin: "CONFIRMED_CANDIDATE" | "ASSERTED";
  candidateId: string | null;
  overrideReason: string | null;
  ownerUserId: string;
  dueDate: string | null;
  dueDateDeclaredAbsent: boolean;
  arisesFromDecisionId: string | null;
  state: "OPEN" | "COMPLETE" | "CANCELLED";
  derivedStatus: CommitmentStatus;
  anchor: FactAnchor | null;
}

export interface CommitmentChange {
  id: string;
  actorId: string;
  changeKind: "STATE" | "OWNER" | "DUE_DATE";
  rationale: string;
  fromValue: string | null;
  toValue: string | null;
  evidence: DocumentReference | null;
  createdAt: string;
}

export interface DocumentReference {
  documentId: string;
  version: number;
  contentHash: string;
}

export interface CommitmentEvidenceInput {
  documentId: string;
  version: number;
  checksum?: string;
}

export interface MeetingExtraction {
  id: string;
  organizationId: string;
  meetingId: string;
  requestedBy: string;
  requestedAt: string;
  completedAt: string;
  provider: string;
  model: string;
  promptVersion: string;
  outcome: "COMPLETED" | "EMPTY" | "FAILED_CLOSED";
  failureCode: string | null;
  failureReason: string | null;
  candidateCount: number;
  droppedUngroundedCount: number;
  sources: { sourceId: string; documentId: string; documentVersion: number; contentHash: string }[];
  candidateNature?: "derived_proposal";
  candidates?: MeetingCandidate[];
}

export type MeetingWorkItemType =
  | "meeting.candidates_awaiting_confirmation"
  | "meeting.minutes_awaiting_adoption"
  | "meeting.commitment_due_soon"
  | "meeting.commitment_overdue";

export interface MeetingWorkItem {
  workItemType: MeetingWorkItemType;
  subjectType: "meeting" | "commitment";
  subjectId: string;
  actionClass: string;
  requiredPermission: string;
  resolutionCondition: string;
  owningApplication: "meeting-ai";
  resolvingEndpoint: string;
}

export interface MeetingAuditEntry {
  id: string;
  action: string;
  actorId: string;
  subjectId: string;
  refusalCode: string | null;
  httpStatus: number | null;
  occurredAt: string;
  sequenceNo: number;
  eventHash: string;
  previousHash: string | null;
}

export interface MeetingAuditIntegrity {
  intact: boolean;
  firstBrokenPosition: number | null;
}

export interface MeetingFilter {
  state?: MeetingState;
  search?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
}

export interface CreateMeetingInput {
  title: string;
  purpose: string;
  kind: "internal" | "customer" | "vendor" | "board" | "governance_review";
  scheduledStart: string;
  timezone: string;
  confidentialityClassification: "public" | "internal" | "confidential" | "restricted";
  actualStart?: string | null;
  actualEnd?: string | null;
}

export interface ConfirmFactInput {
  candidateId?: string;
  statement?: string;
  correctedStatement?: string | null;
  ownerUserId?: string;
  dueDate?: string | null;
  dueDateDeclaredAbsent?: boolean;
  arisesFromDecisionId?: string | null;
  overrideReason?: string;
}

function query(params: Partial<Record<keyof MeetingFilter, string | undefined>>): string {
  const values = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) values.set(key, value);
  }
  const encoded = values.toString();
  return encoded ? `?${encoded}` : "";
}

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function raise(status: number, data: unknown): never {
  const error = (
    data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null
  )?.error;
  throw new ApiError(
    status,
    error?.code ?? "error",
    error?.message ?? "Meeting AI could not complete that request.",
    error?.details,
  );
}

export class MeetingAiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: MeetingClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.getToken = options.getToken ?? (() => null);
    this.onUnauthorized = options.onUnauthorized;
  }

  private async request<T>(
    method: string,
    orgId: string,
    path: string,
    body?: unknown,
  ): Promise<MeetingResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);

    let response: Response;
    try {
      response = await fetch(
        `${this.baseUrl}/organizations/${orgId}/meeting-ai/${path.replace(/^\/+/, "")}`,
        init,
      );
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach Meeting AI. Check your connection.");
    }
    if (response.status === 401) this.onUnauthorized?.();
    const data = await parse(response);
    if (!response.ok) raise(response.status, data);
    return {
      data: data as T,
      demoMode: response.headers.get("x-meeting-ai-mode") === "demo",
      correlationId: response.headers.get("x-correlation-id"),
    };
  }

  meetings(orgId: string, filter: MeetingFilter = {}) {
    return this.request<{ meetings: Meeting[]; nextOffset: number | null }>(
      "GET",
      orgId,
      `meetings${query(filter)}`,
    );
  }
  meeting(orgId: string, id: string) {
    return this.request<{ meeting: Meeting }>("GET", orgId, `meetings/${id}`);
  }
  transition(orgId: string, meetingId: string, input: { targetState: MeetingState; reason: string }) {
    return this.request<{ meeting: Meeting }>("POST", orgId, `meetings/${meetingId}/transitions`, input);
  }
  createMeeting(orgId: string, input: CreateMeetingInput) {
    return this.request<{ meeting: Meeting }>("POST", orgId, "meetings", input);
  }
  participants(orgId: string, meetingId: string) {
    return this.request<{ participants: MeetingParticipant[] }>(
      "GET",
      orgId,
      `meetings/${meetingId}/participants`,
    );
  }
  addParticipant(orgId: string, meetingId: string, input: MeetingParticipantInput) {
    return this.request<{ participant: MeetingParticipant }>(
      "POST",
      orgId,
      `meetings/${meetingId}/participants`,
      input,
    );
  }
  removeParticipant(orgId: string, meetingId: string, participantId: string) {
    return this.request<{ participant: MeetingParticipant }>(
      "DELETE",
      orgId,
      `meetings/${meetingId}/participants/${participantId}`,
    );
  }
  sources(orgId: string, meetingId: string) {
    return this.request<{ sources: MeetingSource[] }>("GET", orgId, `meetings/${meetingId}/sources`);
  }
  attachSource(orgId: string, meetingId: string, input: { documentId: string; documentVersion?: number }) {
    return this.request<{ source: MeetingSource }>("POST", orgId, `meetings/${meetingId}/sources`, input);
  }
  detachSource(orgId: string, meetingId: string, sourceId: string, reason: string) {
    return this.request<{ source: MeetingSource }>(
      "DELETE",
      orgId,
      `meetings/${meetingId}/sources/${sourceId}`,
      { reason },
    );
  }
  extractions(orgId: string, meetingId: string) {
    return this.request<{ extractions: MeetingExtraction[] }>("GET", orgId, `meetings/${meetingId}/extractions`);
  }
  runExtraction(orgId: string, meetingId: string) {
    return this.request<{ extraction: MeetingExtraction }>("POST", orgId, `meetings/${meetingId}/extractions`);
  }
  candidates(orgId: string, meetingId: string) {
    return this.request<{ candidates: MeetingCandidate[] }>("GET", orgId, `meetings/${meetingId}/candidates`);
  }
  rejectCandidate(orgId: string, meetingId: string, candidateId: string, reason: string) {
    return this.request<{ candidate: MeetingCandidate }>(
      "POST",
      orgId,
      `meetings/${meetingId}/candidates/${candidateId}/rejection`,
      { reason },
    );
  }
  confirmDecision(orgId: string, meetingId: string, input: ConfirmFactInput | string) {
    const payload = typeof input === "string" ? { candidateId: input } : input;
    return this.request<{ decision: MeetingDecision }>("POST", orgId, `meetings/${meetingId}/decisions`, payload);
  }
  confirmCommitment(orgId: string, meetingId: string, input: ConfirmFactInput) {
    return this.request<{ commitment: MeetingCommitment }>("POST", orgId, `meetings/${meetingId}/commitments`, input);
  }
  decisions(orgId: string, meetingId: string) {
    return this.request<{ decisions: MeetingDecision[] }>("GET", orgId, `meetings/${meetingId}/decisions`);
  }
  commitments(orgId: string, meetingId?: string) {
    const path = meetingId ? `meetings/${meetingId}/commitments` : "commitments";
    return this.request<{ commitments: MeetingCommitment[] }>("GET", orgId, path);
  }
  commitment(orgId: string, commitmentId: string) {
    return this.request<{ commitment: MeetingCommitment; changes: CommitmentChange[] }>(
      "GET",
      orgId,
      `commitments/${commitmentId}`,
    );
  }
  completeCommitment(orgId: string, id: string, input: { rationale: string; evidence?: CommitmentEvidenceInput }) {
    return this.request<{ commitment: MeetingCommitment }>("POST", orgId, `commitments/${id}/completion`, input);
  }
  cancelCommitment(orgId: string, id: string, rationale: string) {
    return this.request<{ commitment: MeetingCommitment }>("POST", orgId, `commitments/${id}/cancellation`, { rationale });
  }
  changeOwner(orgId: string, id: string, ownerUserId: string, rationale: string) {
    return this.request<{ commitment: MeetingCommitment }>("PATCH", orgId, `commitments/${id}/owner`, { ownerUserId, rationale });
  }
  changeDueDate(orgId: string, id: string, dueDate: string | null, rationale: string) {
    return this.request<{ commitment: MeetingCommitment }>(
      "PATCH",
      orgId,
      `commitments/${id}/due-date`,
      { dueDate, dueDateDeclaredAbsent: dueDate === null, rationale },
    );
  }
  adopt(orgId: string, meetingId: string, rationale: string) {
    return this.request<{ meeting: Meeting }>("POST", orgId, `meetings/${meetingId}/adoption`, { rationale });
  }
  seal(orgId: string, meetingId: string, rationale: string) {
    return this.request<{ meeting: Meeting }>("POST", orgId, `meetings/${meetingId}/sealing`, { rationale });
  }
  links(orgId: string, meetingId: string) {
    return this.request<{ links: unknown[] }>("GET", orgId, `meetings/${meetingId}/links`);
  }
  workItems(orgId: string) {
    return this.request<{ owningApplication: "meeting-ai"; items: MeetingWorkItem[] }>("GET", orgId, "work-items");
  }
  timeline(orgId: string, meetingId: string) {
    return this.request<{ entries: MeetingAuditEntry[]; integrity: MeetingAuditIntegrity }>(
      "GET",
      orgId,
      `meetings/${meetingId}/timeline`,
    );
  }
}
